/**
 * Brokerage Hub — detail screen.
 *
 * One brokerage in full: profile header, four metric tiles, the lead
 * funnel chart, and three tabs — Leads (with status filter + advance /
 * edit / delete actions), Property Performance, and the Activity timeline.
 *
 * Route: /leads-dashboard/brokerage-hub/:id
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Pencil, Plus, Download, Phone, Mail, FileText, MessageCircle,
  ChevronRight, Trash2, Users, CheckCircle2, TrendingUp, Clock, AlertTriangle,
  Building2, Activity as ActivityIcon, FileSpreadsheet, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchBrokerage, fetchBrokerageLeads, fetchBrokerageActivity, fetchBrokerages,
  advanceLeadStatus, deleteLead, brokerageLeadsCsvUrl, brokerageLeadsXlsxUrl,
} from './api/brokerages';
import BrokerageModal from './components/BrokerageModal';
import BrokerageLeadModal from './components/BrokerageLeadModal';
import BrokerageFunnelChart from './components/BrokerageFunnelChart';
import { TIER_STYLES, reraExpiryDays } from './components/BrokerageCard';
import HelpInfo from '../../components/HelpInfo';
import InfoBadge from '@/components/InfoBadge';
import useIsBdm from './hooks/useIsBdm';

const STATUS_PILL = {
  new: 'bg-slate-100 text-slate-700 border-slate-300',
  contacted: 'bg-blue-100 text-blue-700 border-blue-300',
  viewing: 'bg-amber-100 text-amber-700 border-amber-300',
  negotiation: 'bg-orange-100 text-orange-700 border-orange-300',
  booking_initiated: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  won: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  lost: 'bg-red-100 text-red-700 border-red-300',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-300',
};
// MoM 05 Jun 2026 §7 — finance refers to the post-New stage as
// "Prospecting", not "Contacted". We keep the internal value
// ``contacted`` (no DB migration needed) but render this label
// everywhere the user can see it.
const STATUS_LABELS = {
  new: 'New',
  contacted: 'Prospecting',
  viewing: 'Viewing',
  negotiation: 'Negotiation',
  booking_initiated: 'Booking Initiated',
  won: 'Won',
  lost: 'Lost',
  cancelled: 'Cancelled',
};
const TERMINAL = new Set(['won', 'lost', 'cancelled']);

// wa.me needs a bare international number — strip everything non-digit.
const waLink = (phone) => `https://wa.me/${String(phone || '').replace(/\D/g, '')}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');
const fmtMoney = (n) => `AED ${Number(n || 0).toLocaleString()}`;

export default function BrokerageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { token } = useAuth();
  const isBdm = useIsBdm();

  const [brokerage, setBrokerage] = useState(null);
  const [leads, setLeads] = useState([]);
  const [activity, setActivity] = useState([]);
  const [allBrokerages, setAllBrokerages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const [editOpen, setEditOpen] = useState(false);
  const [leadModal, setLeadModal] = useState({ open: false, lead: null });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, leadData, activityData, brokerList] = await Promise.all([
        fetchBrokerage(token, id),
        fetchBrokerageLeads(token, id),
        fetchBrokerageActivity(token, id),
        fetchBrokerages(token, { sort: 'name' }),
      ]);
      setBrokerage(detail);
      setLeads(leadData?.leads || []);
      setActivity(activityData?.activity || []);
      setAllBrokerages(brokerList?.brokerages || []);
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || 'Could not load this brokerage',
      );
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => { reload(); }, [reload]);

  const handleAdvance = async (lead) => {
    try {
      await advanceLeadStatus(token, lead.id);
      toast.success('Lead advanced');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not advance the lead');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLead(token, deleteTarget.id);
      toast.success('Lead deleted');
      setDeleteTarget(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete the lead');
    }
  };

  const exportCsv = () => window.open(brokerageLeadsCsvUrl(token, id), '_blank');
  const exportXlsx = () => window.open(brokerageLeadsXlsxUrl(token, id), '_blank');

  const visibleLeads =
    statusFilter === 'all'
      ? leads
      : leads.filter((l) => (l.status || 'new').toLowerCase() === statusFilter);

  // ---------- loading / error states ----------
  if (loading && !brokerage) {
    return (
      <Shell isDarkMode={isDarkMode}>
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </Shell>
    );
  }

  if (error || !brokerage) {
    return (
      <Shell isDarkMode={isDarkMode}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads-dashboard/brokerage-hub')}>
          <ArrowLeft size={14} className="mr-1.5" /> Back to Brokerage Hub
        </Button>
        <Card>
          <CardContent className="py-14 text-center">
            <AlertTriangle size={32} className="mx-auto text-amber-400 mb-3" />
            <p className="font-medium">{error || 'Brokerage not found'}</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  const stats = brokerage.stats || {};
  const expiryDays = reraExpiryDays(brokerage.rera_expiry);
  const expirySoon = expiryDays !== null && expiryDays <= 60;

  return (
    <Shell isDarkMode={isDarkMode}>
      <Button variant="ghost" size="sm" onClick={() => navigate('/leads-dashboard/brokerage-hub')}>
        <ArrowLeft size={14} className="mr-1.5" /> Back to Brokerage Hub
      </Button>

      {/* ---------- Header ---------- */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-violet-500 text-white flex items-center justify-center text-xl font-bold flex-shrink-0">
                {(brokerage.name || '?').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold">{brokerage.name}</h1>
                  <Badge className={`text-xs border ${TIER_STYLES[brokerage.tier] || TIER_STYLES.Mid}`}>
                    {brokerage.tier || 'Mid'}
                  </Badge>
                  {(brokerage.status || 'Active').toLowerCase() === 'inactive' && (
                    <Badge variant="outline" className="text-xs text-slate-400">Inactive</Badge>
                  )}
                  <HelpInfo
                    align="inline"
                    title="How to use the brokerage detail screen"
                    steps={[
                      <>The header is the firm's profile — <strong>Edit</strong> updates its details, <strong>Add Lead</strong> logs a new referral, and <strong>MoU</strong> opens the signed agreement document.</>,
                      <>The four tiles and the <strong>Lead Funnel</strong> chart show how this brokerage's leads progress from New through to Won.</>,
                      <>The <strong>Leads</strong> tab lists every referred lead — filter by status, click the chevron to advance a lead one stage, the pencil to edit, the bin to delete. A lead's phone number opens straight in WhatsApp.</>,
                      <><strong>Property Performance</strong> shows which projects this brokerage converts best on; <strong>Activity</strong> is the full audit trail of every change.</>,
                      <><strong>Export CSV</strong> downloads this brokerage's leads for offline review.</>,
                    ]}
                    tip="Advance a lead as soon as its real status changes — the funnel and conversion rate are only as accurate as the lead statuses you keep current."
                  />
                </div>
                <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500 flex-wrap">
                  {brokerage.contact_person && <span>{brokerage.contact_person}</span>}
                  {brokerage.phone && (
                    <span className="flex items-center gap-1"><Phone size={12} /> {brokerage.phone}</span>
                  )}
                  {brokerage.email && (
                    <span className="flex items-center gap-1"><Mail size={12} /> {brokerage.email}</span>
                  )}
                  {(brokerage.rera_license || brokerage.rera_number) && (
                    <span className="flex items-center gap-1">
                      <FileText size={12} /> RERA {brokerage.rera_license || brokerage.rera_number}
                    </span>
                  )}
                  {stats.avg_response_hours != null && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> Avg response {stats.avg_response_hours}h
                    </span>
                  )}
                  {brokerage.created_at && (
                    <span className="flex items-center gap-1">
                      <CalendarDays size={12} /> Added {fmtDate(brokerage.created_at)}
                    </span>
                  )}
                </div>
                {expirySoon && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 w-fit">
                    <AlertTriangle size={12} />
                    {expiryDays < 0
                      ? 'RERA licence expired'
                      : `RERA licence expires in ${expiryDays} day${expiryDays === 1 ? '' : 's'}`}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {brokerage.mou_document_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(
                    `${process.env.REACT_APP_BACKEND_URL}${brokerage.mou_document_url}?token=${encodeURIComponent(token)}`,
                    '_blank',
                  )}
                >
                  <FileText size={14} className="mr-1.5" /> MoU
                </Button>
              )}
              {!isBdm && (
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil size={14} className="mr-1.5" /> Edit
                </Button>
              )}
              {!isBdm && (
                <Button size="sm" onClick={() => setLeadModal({ open: true, lead: null })}>
                  <Plus size={14} className="mr-1.5" /> Add Lead
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------- Metric tiles ---------- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric
          icon={Users}
          tone="text-blue-500"
          label="Total Leads"
          value={stats.total_leads ?? 0}
          info="Lifetime count of every lead this brokerage has referred — across all statuses, won + lost + still in pipeline."
        />
        <Metric
          icon={CheckCircle2}
          tone="text-emerald-500"
          label="Converted"
          value={stats.won_leads ?? 0}
          info="Leads from this brokerage that reached a Won status (won / closed_won / converted / booked). The basis for commission calculations."
        />
        <Metric
          icon={TrendingUp}
          tone="text-violet-500"
          label="Conversion Rate"
          value={`${stats.conversion_rate ?? 0}%`}
          info="Converted ÷ Total Leads × 100. Compare this against the portfolio average (in the top KPI strip) to gauge whether the brokerage is over- or under-performing."
        />
        <Metric
          icon={Building2}
          tone="text-amber-500"
          label="Revenue"
          value={fmtMoney(stats.revenue)}
          info="Sum of payout amounts on every Won lead from this brokerage, in AED. Excludes pending or rejected commission rows."
        />
      </div>

      {/* ---------- Funnel ---------- */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            Lead Funnel
            <InfoBadge text="Bar count per lifecycle stage for this brokerage's leads only. Stages: New → Prospecting → Viewing → Negotiation → Booking Initiated → Won, plus Lost as the terminal exit. Helps spot where leads typically drop out." />
          </CardTitle>
          <CardDescription>How this brokerage's leads progress to Won.</CardDescription>
        </CardHeader>
        <CardContent>
          <BrokerageFunnelChart funnel={brokerage.funnel} />
        </CardContent>
      </Card>

      {/* ---------- Tabs ---------- */}
      <Tabs defaultValue="leads">
        <TabsList>
          <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="properties">Property Performance</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* --- Leads tab --- */}
        <TabsContent value="leads">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {['new', 'contacted', 'viewing', 'negotiation', 'won', 'lost'].map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-500">
                  {visibleLeads.length} of {leads.length} leads
                </span>
                <div className="ml-auto flex gap-1.5">
                  <Button variant="outline" size="sm" onClick={exportXlsx} title="Download as Excel (.xlsx) — formatted, frozen header, AED currency">
                    <FileSpreadsheet size={14} className="mr-1.5" /> Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportCsv} title="Download as plain CSV">
                    <Download size={14} className="mr-1.5" /> CSV
                  </Button>
                </div>
              </div>

              {visibleLeads.length === 0 ? (
                <div className="py-12 text-center text-slate-500">
                  <Users size={32} className="mx-auto text-slate-300 mb-2" />
                  <p>{leads.length ? 'No leads match this status.' : 'No leads yet for this brokerage.'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 border-b">
                      <tr>
                        <th className="text-left py-2 px-3">Lead</th>
                        <th className="text-left py-2 px-3">Property</th>
                        <th className="text-left py-2 px-3">Status</th>
                        {!isBdm && <th className="text-left py-2 px-3">Phone</th>}
                        <th className="text-left py-2 px-3">Visit · Docs</th>
                        <th className="text-left py-2 px-3">Added</th>
                        {!isBdm && <th className="text-center py-2 px-3 w-32">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLeads.map((lead) => {
                        const st = (lead.status || 'new').toLowerCase();
                        return (
                          <tr
                            key={lead.id}
                            className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${isBdm ? '' : 'cursor-pointer'}`}
                            onClick={isBdm ? undefined : () => setLeadModal({ open: true, lead })}
                          >
                            <td className="py-2 px-3 font-medium">{lead.client_name || '—'}</td>
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-300">
                              {lead.project_name || '—'}
                            </td>
                            <td className="py-2 px-3">
                              <Badge className={`text-xs border ${STATUS_PILL[st] || STATUS_PILL.new}`}>
                                {STATUS_LABELS[st] || st}
                              </Badge>
                            </td>
                            {!isBdm && (
                              <td className="py-2 px-3">
                                {lead.client_mobile ? (
                                  <a
                                    href={waLink(lead.client_mobile)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
                                    title="Open in WhatsApp"
                                  >
                                    <MessageCircle size={13} />
                                    {lead.client_mobile}
                                  </a>
                                ) : '—'}
                              </td>
                            )}
                            <td className="py-2 px-3">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {lead.site_visit_date ? (
                                  <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-700">
                                    <CalendarDays size={10} className="mr-1" />
                                    {new Date(lead.site_visit_date).toLocaleDateString()}
                                  </Badge>
                                ) : (
                                  <span className="text-[11px] text-slate-400">No visit</span>
                                )}
                                {Array.isArray(lead.documents) && lead.documents.length > 0 ? (
                                  <Badge variant="outline" className="text-[10px] bg-violet-50 border-violet-200 text-violet-700">
                                    <FileText size={10} className="mr-1" />
                                    {lead.documents.length} doc{lead.documents.length === 1 ? '' : 's'}
                                  </Badge>
                                ) : (
                                  <span className="text-[11px] text-slate-400">No docs</span>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-xs text-slate-500 whitespace-nowrap">
                              {lead.created_at ? fmtDate(lead.created_at) : '—'}
                            </td>
                            {!isBdm && (
                              <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm" variant="ghost" className="h-7 w-7 p-0"
                                    title={TERMINAL.has(st) ? 'Lead is closed' : 'Advance status'}
                                    disabled={TERMINAL.has(st)}
                                    onClick={() => handleAdvance(lead)}
                                  >
                                    <ChevronRight size={14} />
                                  </Button>
                                  <Button
                                    size="sm" variant="ghost" className="h-7 w-7 p-0"
                                    title="Edit lead"
                                    onClick={() => setLeadModal({ open: true, lead })}
                                  >
                                    <Pencil size={13} />
                                  </Button>
                                  <Button
                                    size="sm" variant="ghost"
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                                    title="Delete lead"
                                    onClick={() => setDeleteTarget(lead)}
                                  >
                                    <Trash2 size={13} />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Property performance tab --- */}
        <TabsContent value="properties">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileSpreadsheet size={16} /> Property Performance
              </CardTitle>
              <CardDescription>Which properties this brokerage converts best on.</CardDescription>
            </CardHeader>
            <CardContent>
              {brokerage.property_breakdown?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 border-b">
                      <tr>
                        <th className="text-left py-2 px-3">Property</th>
                        <th className="text-center py-2 px-3">Total Leads</th>
                        <th className="text-center py-2 px-3">Won</th>
                        <th className="text-center py-2 px-3">Conversion</th>
                        <th className="text-right py-2 px-3">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brokerage.property_breakdown.map((p) => (
                        <tr key={p.property} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-2 px-3 font-medium">{p.property}</td>
                          <td className="py-2 px-3 text-center">{p.total}</td>
                          <td className="py-2 px-3 text-center text-emerald-600">{p.won}</td>
                          <td className="py-2 px-3 text-center">{p.conversion_rate}%</td>
                          <td className="py-2 px-3 text-right font-mono">{fmtMoney(p.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center text-slate-500">
                  No property data yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Activity tab --- */}
        <TabsContent value="activity">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ActivityIcon size={16} /> Activity Timeline
              </CardTitle>
              <CardDescription>Every change made to this brokerage and its leads.</CardDescription>
            </CardHeader>
            <CardContent>
              {activity.length ? (
                <div className="space-y-3">
                  {activity.map((a) => (
                    <div key={a.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-violet-500 mt-1.5" />
                        <div className="flex-1 w-px bg-slate-200 dark:bg-slate-700" />
                      </div>
                      <div className="pb-3">
                        <p className="text-sm text-slate-800 dark:text-slate-200">{a.description}</p>
                        <p className="text-xs text-slate-400">
                          {a.performed_by_name ? `${a.performed_by_name} · ` : ''}{fmtDate(a.performed_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-slate-500">No activity recorded yet.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ---------- Modals ---------- */}
      <BrokerageModal
        open={editOpen}
        onOpenChange={setEditOpen}
        brokerage={brokerage}
        onSaved={reload}
      />
      <BrokerageLeadModal
        open={leadModal.open}
        onOpenChange={(v) => setLeadModal((s) => ({ ...s, open: v }))}
        lead={leadModal.lead}
        brokerages={allBrokerages}
        defaultBrokerageId={id}
        onSaved={reload}
      />

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete this lead?</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.client_name || 'This lead'}" will be permanently removed.
              This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-500 text-white"
              onClick={handleDelete}
            >
              Delete lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

const Shell = ({ isDarkMode, children }) => (
  <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`}>
    <div className="max-w-[1400px] mx-auto p-6 space-y-5">{children}</div>
  </div>
);

const Metric = ({ icon: Icon, tone, label, value, info }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Icon size={14} className={tone} />
        <span className="flex-1">{label}</span>
        <InfoBadge text={info} />
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </CardContent>
  </Card>
);
